import * as React from "react"

import type {
  ToastActionElement,
  ToastProps,
} from "@/components/ui/toast"

const TOAST_LIMIT = 1
const TOAST_REMOVE_DELAY = 1000000
const LEVEL_UP_TOAST_DURATION = 5000 // 5 seconds for level up toasts to display fully

type ToastPriority = 'high' | 'normal'

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  priority?: ToastPriority
  isLevelUp?: boolean
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type State = {
  toasts: ToasterToast[]
  queue: ToasterToast[] // Queue for toasts waiting to be shown
  isShowingLevelUp: boolean // Flag to track if a level-up toast is currently showing
}

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
const levelUpTimers = new Map<string, ReturnType<typeof setTimeout>>()

let queueProcessor: ReturnType<typeof setTimeout> | null = null

// Function to check if a toast is a level-up notification
const isLevelUpToast = (toast: ToasterToast): boolean => {
  return toast.isLevelUp || 
         (toast.title === '🎉 Level Up!' || toast.title === 'Level Up!') || 
         (typeof toast.title === 'string' && toast.title.includes('Level Up'));
}

// Function to process the queue
const processQueue = (state: State): State => {
  if (state.queue.length === 0 || state.toasts.length >= TOAST_LIMIT) {
    return state;
  }

  // Sort queue by priority first, then by time added
  const sortedQueue = [...state.queue].sort((a, b) => {
    if (a.priority === 'high' && b.priority !== 'high') return -1;
    if (a.priority !== 'high' && b.priority === 'high') return 1;
    return 0;
  });

  // Take the first toast from the queue
  const nextToast = sortedQueue[0];
  const remainingQueue = sortedQueue.slice(1);

  // Check if it's a level-up toast
  const isLevelUp = isLevelUpToast(nextToast);

  return {
    ...state,
    toasts: [...state.toasts, nextToast].slice(0, TOAST_LIMIT),
    queue: remainingQueue,
    isShowingLevelUp: isLevelUp
  };
}

const addToRemoveQueue = (toastId: string, state: State, dispatch: React.Dispatch<Action>) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const toast = state.toasts.find(t => t.id === toastId);
  const isLevelUp = toast ? isLevelUpToast(toast) : false;
  
  // Set a longer delay for level-up toasts
  const delay = isLevelUp ? LEVEL_UP_TOAST_DURATION : TOAST_REMOVE_DELAY;

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })

    // If it was a level-up toast, wait a bit before showing the next toast
    if (isLevelUp) {
      const levelUpTimer = setTimeout(() => {
        levelUpTimers.delete(toastId);
        // Process the queue after the level-up toast is gone
        dispatch({ type: "ADD_TOAST", toast: { id: "process-queue", open: false } });
      }, 1000); // 1-second delay before showing the next toast
      
      levelUpTimers.set(toastId, levelUpTimer);
    } else {
      // Process the queue immediately for non-level-up toasts
      dispatch({ type: "ADD_TOAST", toast: { id: "process-queue", open: false } });
    }
  }, delay)

  toastTimeouts.set(toastId, timeout)
}

const initialState: State = {
  toasts: [],
  queue: [],
  isShowingLevelUp: false
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST": {
      // Special case for the queue processor signal
      if (action.toast.id === "process-queue") {
        return processQueue(state);
      }

      const toast = action.toast;
      const isLevelUp = isLevelUpToast(toast);

      // Check if we can add the toast directly or need to queue it
      if (state.toasts.length < TOAST_LIMIT && !state.isShowingLevelUp) {
        return {
          ...state,
          toasts: [toast, ...state.toasts].slice(0, TOAST_LIMIT),
          isShowingLevelUp: isLevelUp
        };
      } else if (isLevelUp && state.isShowingLevelUp) {
        // If it's a level-up toast and we're already showing one, prioritize in queue
        return {
          ...state,
          queue: [{ ...toast, priority: 'high' }, ...state.queue]
        };
      } else {
        // Add to queue with appropriate priority
        return {
          ...state,
          queue: [...state.queue, { 
            ...toast, 
            priority: isLevelUp ? 'high' : 'normal' 
          }]
        };
      }
    }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
        queue: state.queue.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        )
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // Side effects - dismiss the toast
      if (toastId) {
        addToRemoveQueue(toastId, state, dispatch)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id, state, dispatch)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }

    case "REMOVE_TOAST": {
      if (action.toastId === undefined) {
        // Clear all toasts and timers
        toastTimeouts.forEach((timeout) => clearTimeout(timeout))
        toastTimeouts.clear()
        levelUpTimers.forEach((timeout) => clearTimeout(timeout))
        levelUpTimers.clear()
        
        return {
          ...state,
          toasts: [],
          isShowingLevelUp: false
        }
      }

      // Find the toast being removed to check if it's a level-up toast
      const removedToast = state.toasts.find(t => t.id === action.toastId);
      const wasLevelUp = removedToast ? isLevelUpToast(removedToast) : false;

      // Remove the toast
      const newState = {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
        isShowingLevelUp: state.isShowingLevelUp && !wasLevelUp // Update level-up flag
      };

      // Process the queue if there's space
      if (newState.toasts.length < TOAST_LIMIT && newState.queue.length > 0) {
        return processQueue(newState);
      }

      return newState;
    }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = initialState

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

type Toast = Omit<ToasterToast, "id">

function toast({ ...props }: Toast) {
  const id = genId()

  // Check if it's a level-up toast
  const isLevelUp = isLevelUpToast(props as ToasterToast);
  const priority: ToastPriority = isLevelUp ? 'high' : 'normal';

  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id },
    })
  const dismiss = () => dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: {
      ...props,
      id,
      open: true,
      priority,
      isLevelUp,
      onOpenChange: (open) => {
        if (!open) dismiss()
      },
    },
  })

  return {
    id: id,
    dismiss,
    update,
  }
}

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
